import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Skeleton,
  Typography,
} from '@mui/material';
import { CardItem } from './CardItem';
import { MerchantInsightsMetrics } from '../../../store/modules/insights/types';
import { formatChartValue } from '../../../util/insightsUtils';

interface MetricCardProps {
  title: string;
  subtitle?: string;
  metrics?: MerchantInsightsMetrics | null;
  hasData?: boolean;
  isLoading?: boolean;
  isParentMerchant?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  subtitle,
  metrics,
  hasData = true,
  isLoading = false,
  isParentMerchant = false,
}) => {
  const theme = useTheme();
  const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const shouldUseMobileLayout = isMobileBreakpoint;

  if (isLoading) {
    return <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '8px' }} />;
  }

  if (!hasData || !metrics) {
    return (
      <Card
        sx={{
          height: '100%',
          border: '1px solid',
          borderColor: 'secondary.main',
          borderRadius: '8px',
          backgroundColor: 'background.paper',
          boxShadow: 'none',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.5,
            }}
          >
            <Typography variant="xl" component="h3">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="medium" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="huge" fontWeight="semibold" color="text.secondary">
              -
            </Typography>
            <Typography variant="medium" color="text.secondary">
              No Data
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'secondary.main',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'baseline',
            gap: 1.5,
          }}
        >
          <Typography variant="xl" component="h3">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="medium" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        <Grid container spacing={{ xs: 1, md: 2 }}>
          <Grid item xs={12}>
            <CardItem
              size={shouldUseMobileLayout ? 'medium' : 'large'}
              metric={formatChartValue(metrics?.gmv, 'gmv')}
              label="Gross Merchandise Value (GMV)"
              tooltip="Total value of merchandise sold"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CardItem
              size={shouldUseMobileLayout ? 'small' : 'medium'}
              metric={formatChartValue(metrics?.transactionCount, 'transactionCount')}
              label="Transaction Count"
              tooltip="Number of successful transactions"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CardItem
              size={shouldUseMobileLayout ? 'small' : 'medium'}
              metric={formatChartValue(metrics?.averageOrderValue, 'averageOrderValue')}
              label="Avg. order value (AOV)"
              tooltip="Average value per order"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CardItem
              size={shouldUseMobileLayout ? 'small' : 'medium'}
              metric={formatChartValue(metrics?.authorizationRate, 'authorizationRate')}
              label="Authorization Rate"
              tooltip="Percentage of successful authorizations"
            />
          </Grid>

          {isParentMerchant && (
            <Grid item xs={12} md={6}>
              <CardItem
                size={shouldUseMobileLayout ? 'small' : 'medium'}
                metric={formatChartValue(metrics?.contributingMerchants, 'contributingMerchants')}
                label="Contributing Merchants"
                tooltip="Number of child merchants contributing to these metrics"
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <CardItem
              size={shouldUseMobileLayout ? 'small' : 'medium'}
              metric={formatChartValue(metrics?.refundGmv, 'refundGmv')}
              label="Successful Refund Value"
              tooltip="Total value of successful refunds in USD"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CardItem
              size={shouldUseMobileLayout ? 'small' : 'medium'}
              metric={formatChartValue(metrics?.refundTransactionCount, 'refundTransactionCount')}
              label="Refund Transaction Count"
              tooltip="Number of refund transactions"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
